import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { t3, pick3 } from "../i18n/v3";
import { PageShell, PageHeader, EmptyState } from "../components/PageShell";
import PageBackdrop from "../components/PageBackdrop";
import useJsonLd from "../hooks/useJsonLd";
import { PROJECTS } from "../data/projects";
import { API, pickContent } from "../lib/content";

const SITE = "https://lifeisaproject.global";
const ease = [0.16, 1, 0.3, 1];

/**
 * ProjectsPage — lists published projects from the CMS (database is the
 * source of truth). The legacy static registry in src/data/projects.js
 * is kept as a fallback and rendered alongside. Drafts never appear.
 * Never expose Atlas, YOUR1PLACE or any private/unfinished initiatives.
 */
export default function ProjectsPage() {
    const { lang } = useLanguage();
    const [cmsProjects, setCmsProjects] = useState([]);

    useEffect(() => {
        axios.get(`${API}/content/projects`).then((r) => setCmsProjects(r.data || [])).catch(() => {});
    }, []);

    const total = cmsProjects.length + PROJECTS.length;

    useJsonLd("itemlist-projects", total ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Projects — Life is a Project",
        url: `${SITE}/projects`,
        numberOfItems: total,
        itemListElement: [
            ...cmsProjects.map((p, i) => ({
                "@type": "ListItem", position: i + 1,
                url: `${SITE}/projects/${p.slug}`, name: p.title,
            })),
            ...PROJECTS.map((p, i) => ({
                "@type": "ListItem", position: cmsProjects.length + i + 1,
                url: `${SITE}/projects/${p.slug}`, name: p.title?.en || p.slug,
            })),
        ],
    } : null);

    return (
        <PageShell testId="page-projects" backdrop={<PageBackdrop variant="projects" />}>
            <PageHeader
                overline={pick3(t3.projects.overline, lang)}
                title={pick3(t3.projects.title, lang)}
                kicker={pick3(t3.projects.lede, lang)}
            />
            <p className="max-w-[62ch] text-[16px] md:text-[17px] leading-[1.7] text-[color:var(--navy)]/85 mb-16">
                {pick3(t3.projects.body, lang)}
            </p>

            {total === 0 ? (
                <EmptyState label={pick3(t3.projects.empty, lang)} />
            ) : (
                <ul data-testid="projects-list" className="border-t border-[color:var(--line-strong)]">
                    {cmsProjects.map((p, i) => {
                        const { title, subtitle } = pickContent(p, lang);
                        return (
                            <motion.li
                                key={p.slug}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-8%" }}
                                transition={{ duration: 0.7, ease, delay: i * 0.05 }}
                                className="border-b border-[color:var(--line)]"
                            >
                                <Link
                                    to={`/projects/${p.slug}`}
                                    data-testid="project-card"
                                    className="grid grid-cols-12 gap-6 py-9 items-baseline group"
                                >
                                    <div className="col-span-3 md:col-span-2 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                                        {p.period || p.publication_date || ""}
                                    </div>
                                    <div className="col-span-9 md:col-span-7">
                                        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--horizon)] mb-2">
                                            {[p.project_status, p.category].filter(Boolean).join(" · ")}
                                        </div>
                                        <h3 className="font-display text-[26px] md:text-[36px] leading-[1.08] tracking-[-0.01em] text-[color:var(--navy)] group-hover:italic transition-all duration-500">
                                            {title}
                                        </h3>
                                        {subtitle && (
                                            <p className="mt-3 max-w-[60ch] text-[15px] md:text-[16px] leading-[1.6] text-[color:var(--navy)]/75">
                                                {subtitle}
                                            </p>
                                        )}
                                    </div>
                                    <div className="col-span-12 md:col-span-3 md:text-right font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)] flex items-center md:justify-end gap-3">
                                        {p.location && <span>{p.location}</span>}
                                        <span data-testid="content-lang-badge" className="text-[10px] text-[color:var(--navy)]/50 border border-[color:var(--line)] px-1.5 py-0.5">
                                            {(p.language || "en").toUpperCase()}
                                        </span>
                                        <span aria-hidden className="w-4 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-8" />
                                    </div>
                                </Link>
                            </motion.li>
                        );
                    })}
                    {PROJECTS.map((p, i) => (
                        <motion.li
                            key={p.slug}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-8%" }}
                            transition={{ duration: 0.7, ease, delay: (cmsProjects.length + i) * 0.05 }}
                            className="border-b border-[color:var(--line)]"
                        >
                            <Link
                                to={`/projects/${p.slug}`}
                                data-testid="project-card"
                                className="grid grid-cols-12 gap-6 py-9 items-baseline group"
                            >
                                <div className="col-span-3 md:col-span-2 font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)]">
                                    {p.number}
                                </div>
                                <div className="col-span-9 md:col-span-7">
                                    <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--horizon)] mb-2">
                                        {p.status}{p.year ? ` · ${p.year}` : ""}
                                    </div>
                                    <h3 className="font-display text-[26px] md:text-[36px] leading-[1.08] tracking-[-0.01em] text-[color:var(--navy)] group-hover:italic transition-all duration-500">
                                        {pick3(p.title, lang)}
                                    </h3>
                                    {p.summary && (
                                        <p className="mt-3 max-w-[60ch] text-[15px] md:text-[16px] leading-[1.6] text-[color:var(--navy)]/75">
                                            {pick3(p.summary, lang)}
                                        </p>
                                    )}
                                </div>
                                <div className="col-span-12 md:col-span-3 md:text-right font-mono text-[11px] tracking-[0.24em] uppercase text-[color:var(--warm-gray-2)] flex items-center md:justify-end gap-3">
                                    {p.location && <span>{p.location}</span>}
                                    <span aria-hidden className="w-4 h-px bg-[color:var(--gold)] transition-all duration-500 group-hover:w-8" />
                                </div>
                            </Link>
                        </motion.li>
                    ))}
                </ul>
            )}
        </PageShell>
    );
}
